import appLogger from '@shared/logging/appLogger';
import { useAuth } from '@shared/providers/AuthProvider';
import { useCallback, useEffect, useState } from 'react';

import User from '../types/user';

/**
 * Get the user's offers from Adobe Target
 * @returns An object containing the current adobe target offers. Recommendation is to listen to state changes on the offers.
 */
export default function useAdobeTarget() {
    const [targetOffers, setTargetOffers] = useState<AdobeTargetOfferResponse>(undefined);
    const { getUser } = useAuth();

    const getTargetOffers = useCallback(async (currentUser: User) => {
        const { adobe } = window;
        const response = await adobe.target.getOffers({
            request: {
                execute: {
                    pageLoad: {
                        parameters: {
                            'auctions.user.id': currentUser.consumerResourceId,
                            'auctions.user.email': currentUser.username,
                        },
                    },
                },
            },
        });

        if (response) {
            await adobe.target.applyOffers({ response: response });
            return response;
        }
    }, []);

    useEffect(() => {
        const callAdobeTargetWhenLoaded = async () => {
            let user = getUser();
            try {
                await waitUntil(() => {
                    user = getUser();
                    return window.adobe?.target && user ? true : false;
                });
                const response = await getTargetOffers(user);
                setTargetOffers(response);
            } catch (e) {
                const traceMessage = {
                    message: 'Error occurred in useAdobeTarget',
                    properties: {
                        condition: `window.adobe.target: ${
                            window.adobe?.target ? true : false
                        }, user: ${user ? true : false}`,
                        error: e,
                    },
                };

                appLogger.logTrace(traceMessage);
            }
        };
        callAdobeTargetWhenLoaded();
    }, [getUser, getTargetOffers, setTargetOffers]);

    return {
        targetOffers,
    };
}

/**
 * Creates a promise that will resolve or reject on condition or timeout (retryCount * retryMS)
 * @param condition Boolean condition function to resolve promise
 * @param retryCount Number of times the condition is called before rejecting
 * @param retryMS Interval between retires
 */
async function waitUntil(condition: () => boolean, retryCount = 100, retryMS = 100) {
    const originalTimer = retryCount * retryMS;
    if (!condition()) {
        await new Promise<void>((resolve, reject) => {
            const interval = setInterval(() => {
                if (condition()) {
                    clearInterval(interval);
                    resolve();
                } else if (retryCount-- == 0) {
                    clearInterval(interval);
                    const error = new Error(`waitUntil expired after ${originalTimer}ms`);
                    reject(error);
                }
            }, retryMS);
        });
    }
}
